import { AvatarImage } from './AvatarImage'
import { IconSvg } from './IconSvg'
import clsx from 'clsx'
import { UserTier } from 'graphql/generated/scheme'
import { getUserDisplayInitials } from 'utils/user-initials'
import { resolveAvatarFallbackSrc, resolveAvatarSrc } from 'utils/avatar'
import { resolveDisplayName } from 'utils/displayName'
import { useStore } from 'hooks/useStore'

export interface UserAvatarProps {
	user: any
	className?: string
	verifiedIconSize?: number
}

export function UserAvatar({ user, className, verifiedIconSize = 16 }: Readonly<UserAvatarProps>) {
	const { user: currentUser } = useStore()
	// When rendering the current user from a query that lacks avatarSource (e.g. event createdBy),
	// overlay the store's fully-resolved user so the chosen avatar preference is honoured.
	const effectiveUser =
		user?.userId && currentUser?.userId && user.userId === currentUser.userId ? currentUser : user
	const resolvedSrc = resolveAvatarSrc(effectiveUser)
	const fallbackSrc = resolveAvatarFallbackSrc(effectiveUser)
	const hasPhoto = Boolean(String(resolvedSrc ?? '').trim())
	const initials = getUserDisplayInitials(user)
	const displayName = resolveDisplayName(effectiveUser)
	const label = displayName?.trim() || user?.userName?.trim() || 'Member'

	return (
		<div className={clsx('relative flex-shrink-0 w-10 h-10 [container-type:inline-size]', className)}>
			{hasPhoto ? (
				<AvatarImage
					className={clsx('h-full w-full rounded-full object-cover')}
					src={resolvedSrc}
					fallbackSrc={fallbackSrc}
					alt={displayName ?? user?.name}
				/>
			) : (
				<div
					className={clsx(
						'flex h-full w-full items-center justify-center rounded-full font-title font-semibold uppercase leading-none tracking-tight',
						'text-[50cqw]',
						'ring-1 ring-inset ring-white/[0.12] bg-gradient-to-br from-secondary-surface to-primary-surface',
						'text-primary-text shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
					)}
					aria-label={label}
					role="img"
				>
					{initials}
				</div>
			)}
			{user?.isVerified && (
				<div className="absolute bottom-0 right-0">
					<IconSvg
						icon="verified"
						className={clsx('text-accent-text', {
							'!text-yellow-400': UserTier.Premium === user?.tier,
						})}
						width={verifiedIconSize}
						height={verifiedIconSize}
					/>
				</div>
			)}
		</div>
	)
}
